import { Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import InputText from "../../../components/Forms/Element/InputText";
import StatusCheckBox from "../../../components/Common/StatusCheckbox";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import ImagePreview from "../../../components/Common/ImagePreview";
import Common from "../../../../constants/Common";
import { downloadUploadFile } from "../../../../services/AxiosService";
import "react-datepicker/dist/react-datepicker.css";
import PhoneNumberInput from "../../../components/Common/PhoneNumberInput";
import GenderSelect from "../../../components/Common/GenderSelect";
import InputDate from "../../../components/Forms/Element/InputDate";
import { convertArrayBufferToBlob, convertGenderToTranslationId, convertStatusToBoolean } from "../../../../utils/ConverterUtils";
import Api from "../../../../constants/Api";
import CpfInput from "../../../components/Common/CpfInput";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import { parseToUTCDate } from "../../../../utils/DateUtils";
import AddressList from "../../../components/Custom/Common/Address/AddressList";
import FormSection from "../../../components/Common/Form/FormSection";
import CustomerSubscriptionSummary from "../../../components/Custom/Main/Customer/CustomerSubscriptionSummary";

const Form = (props) => {

    const { t } = useTranslation();
    const {
        status,
        setStatus,
        formik,
        entityData,
        setUploadedFile,
        uploadedFile,
        isBranchFixed,
        requireBranchInCustomer,
        requireCpfInCustomer
    } = props;
    const [showLoading, setShowLoading] = useState(false);

    useEffect(() => {
        // Preenche os valores nos campos.
        if (entityData != null) {
            formik.setFieldValue('firstName', entityData.firstName);
            formik.setFieldValue('lastName', entityData.lastName || '');
            formik.setFieldValue('phone', entityData.phone);
            formik.setFieldValue('birthDate', entityData.birthDate ? parseToUTCDate(entityData.birthDate) : null);
            formik.setFieldValue('document', entityData.document);
            formik.setFieldValue('email', entityData.email);

            // Preenche o select
            if (entityData.gender !== null) {
                formik.setFieldValue('gender', {
                    value: entityData.gender,
                    label: t(convertGenderToTranslationId(entityData.gender))
                });
            }
            if (entityData.branch !== null) {
                formik.setFieldValue('branch', {
                    value: entityData.branch.id,
                    label: entityData.branch.name
                });
            }

            // Se possuir imagem cadastrada, exibe-a na tela.
            if (entityData.uploadedFileLocation !== null) {
                setShowLoading(true)
                downloadUploadFile(Api.main.customer + "/download", entityData.id)
                    .then(resp => {
                        if (resp.data) {
                            setUploadedFile(convertArrayBufferToBlob(resp.data));
                        }
                    })
                    .catch(() => {
                        // Arquivo não encontrado no S3 (ex.: registro antigo) - não trava o usuário com erro.
                    })
                    .finally(() => setShowLoading(false));
            }

            setStatus(convertStatusToBoolean(entityData.status));
        }

        // eslint-disable-next-line
    }, [entityData]);

    return (
        <>
            <div className="form-layout form-layout--spaced">
                <FormSection
                    title={t('basic_information')}
                    description={t('customer_basic_information_hint')}
                    icon="fa-solid fa-user"
                >
                    <Row className="g-3 align-items-start">
                        <Col lg={2} md={3}>
                            <div className="avatar-upload-wrap">
                                <ImagePreview
                                    id="file"
                                    permittedWidth={Common.PROFILE_IMAGE_PERMITTED_WIDTH}
                                    permittedHeight={Common.PROFILE_IMAGE_PERMITTED_HEIGHT}
                                    permittedSize={Common.PROFILE_IMAGE_PERMITTED_FILE_SIZE}
                                    setUploadedFile={setUploadedFile}
                                    uploadedFile={uploadedFile}
                                    showLoading={showLoading}
                                />
                                <span className="avatar-upload-label">{t('picture')}</span>
                            </div>
                        </Col>
                        <Col lg={10} md={9}>
                            <Row className="g-3">
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('firstName')} <AsteriskRequired /></FormLabel>
                                        <InputText
                                            name="firstName"
                                            placeholder={t('firstName_placeholder')}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.firstName}
                                            error={formik.errors.firstName}
                                            maxLength={30}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('lastName')}</FormLabel>
                                        <InputText
                                            name="lastName"
                                            placeholder={t('lastName_placeholder')}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.lastName}
                                            error={formik.errors.lastName}
                                            maxLength={30}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('phone')} <AsteriskRequired /></FormLabel>
                                        <PhoneNumberInput
                                            name="phone"
                                            formik={formik}
                                            error={formik.errors.phone}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('cpf')} {requireCpfInCustomer && <AsteriskRequired />}</FormLabel>
                                        <CpfInput
                                            name="document"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.document}
                                            error={formik.errors.document}
                                            placeholder={t('document')}
                                            maxlenght="30"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    title={t('addresses')}
                    description={t('customer_addresses_hint')}
                    icon="fa-solid fa-location-dot"
                >
                    {entityData?.id ? (
                        <AddressList parentId={entityData.id} />
                    ) : (
                        <p className="mb-0 text-muted">{t('customer_addresses_save_first_hint')}</p>
                    )}
                </FormSection>

                <FormSection
                    title={t('optional_fields')}
                    description={t('customer_additional_information_hint')}
                    icon="fa-solid fa-circle-info"
                >
                    <Row className="g-3">
                        <Col md={6} xl={4}>
                            <FormGroup>
                                <FormLabel>{t('email')}</FormLabel>
                                <InputText
                                    name="email"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.email}
                                    error={formik.errors.email}
                                    placeholder={t('email_placeholder')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6} xl={3}>
                            <FormGroup>
                                <FormLabel>{t('gender')}</FormLabel>
                                <GenderSelect
                                    name="gender"
                                    value={formik.values.gender}
                                    onChange={(value) =>
                                        formik.setFieldValue('gender', value)
                                    }
                                    onBlur={formik.handleBlur}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6} xl={2}>
                            <FormGroup>
                                <FormLabel>{t('birthDate')}</FormLabel>
                                <InputDate
                                    name="birthDate"
                                    selected={formik.values.birthDate}
                                    onChange={(value) =>
                                        formik.setFieldValue('birthDate', value)
                                    }
                                    onBlur={formik.handleBlur}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6} xl={3}>
                            <FormGroup>
                                <FormLabel>{t('branch')} {requireBranchInCustomer && <AsteriskRequired />}</FormLabel>
                                <BranchSelect
                                    name="branch"
                                    value={formik.values.branch}
                                    onChange={(value) => {
                                        formik.setFieldValue('branch', value);
                                    }}
                                    error={formik.errors.branch}
                                    onBlur={formik.handleBlur}
                                    isDisabled={isBranchFixed}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

                {/* Só na edição: cliente que ainda não existe não tem assinatura para mostrar.
                    O bloco não aparece para quem não é do clube, que é a maioria. */}
                {entityData?.id && <CustomerSubscriptionSummary customerId={entityData.id} />}

                <FormSection
                    title={t('status')}
                    description={t('customer_status_hint')}
                    icon="fa-solid fa-toggle-on"
                    compact
                >
                    <Row>
                        <Col md={12}>
                            <FormGroup className="mb-0">
                                <StatusCheckBox
                                    name="status"
                                    defaultChecked={status}
                                    checked={status}
                                    onClick={() => setStatus(!status)} />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>
            </div>
        </>
    );
}

export default Form;
